/**
 * Os dois nomes de um mesmo CPF/CNPJ são a MESMA pessoa (grafia diferente) ou
 * pessoas DIFERENTES? (#6155)
 *
 * O Sicoob guarda um nome por CPF/CNPJ. Emitir com o documento de um cliente e o
 * nome de outro deixa o banco com o nome errado em todos os boletos daquele
 * documento. Caso que originou: em 30/09/2026 a fatura 13569 (Vera Lucia Elias de
 * Oliveira) saiu com o CPF do Thiago Barbosa Meira — titular da conta da Equatorial
 * — e o comprovante do Thiago passou a mostrar "Vera". O aviso de conflito existia,
 * mas aceitava "é o mesmo cliente, emitir" para qualquer par de nomes.
 *
 * No levantamento de 01/10/2026 eram 163 documentos com mais de um nome nos
 * boletos: 39 só de grafia (acento, "SOUSA/SOUZA", sobrenome a mais, "LTDA" a mais,
 * apelido entre parênteses) e 124 com pessoas diferentes. A regra separa os dois:
 * - mesmo primeiro nome (tolerando 1–2 letras trocadas), ou
 * - metade ou mais das palavras do nome mais curto aparecem no outro (aceitando
 *   abreviação: "ASSOC FUNC DA COMPLEM" = "ASSOCIACAO DOS FUNCIONARIOS DA COMPLEM").
 * - iniciais: "R R FREIRE LANCHONETE" = "RAFAEL RODRIGUES FREIRE" (#6160, empresa
 *   com as iniciais do dono; nenhum dos 124 casos do levantamento muda com isso).
 * Na dúvida fica como "mesma pessoa": cai no aviso de antes, nunca bloqueia à toa.
 */

const LIGACOES = new Set([
    'DE', 'DA', 'DO', 'DAS', 'DOS', 'E', 'D',
    'LTDA', 'LTD', 'ME', 'EPP', 'EIRELI', 'EIREL', 'SA', 'S', 'A', 'MEI', 'CIA',
]);

/** Palavras significativas do nome: sem acento, sem pontuação, sem "DE/DA/LTDA". */
export function palavrasDoNome(nome: string | null | undefined): string[] {
    return String(nome ?? '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9 ]+/g, ' ')
        .split(/\s+/)
        .filter(p => p && !LIGACOES.has(p));
}

function distancia(a: string, b: string): number {
    if (a === b) return 0;
    const ant = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        let diag = ant[0];
        ant[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const tmp = ant[j];
            ant[j] = Math.min(ant[j] + 1, ant[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
            diag = tmp;
        }
    }
    return ant[b.length];
}

/** Mesma palavra com erro de digitação: até 1 letra (palavras curtas) ou 2 (6+ letras). */
function palavraParecida(a: string, b: string): boolean {
    if (a === b) return true;
    const menor = Math.min(a.length, b.length);
    if (menor < 4) return false;
    return distancia(a, b) <= (menor >= 6 ? 2 : 1);
}

/** Abreviação: "ASSOC" de "ASSOCIACAO", "FUNC" de "FUNCIONARIOS". Só na contagem de palavras em comum. */
function abreviacao(a: string, b: string): boolean {
    const [c, l] = a.length <= b.length ? [a, b] : [b, a];
    return c.length >= 4 && l.startsWith(c);
}

/** Letra solta é a inicial de uma palavra do outro nome ("R" de "RAFAEL"). */
function inicial(a: string, b: string): boolean {
    return (a.length === 1 && b.startsWith(a)) || (b.length === 1 && a.startsWith(b));
}

/** True quando os dois nomes parecem ser da mesma pessoa/empresa. Nome vazio conta como "mesma". */
export function nomesParecemMesmaPessoa(a: string | null | undefined, b: string | null | undefined): boolean {
    const pa = palavrasDoNome(a);
    const pb = palavrasDoNome(b);
    if (pa.length === 0 || pb.length === 0) return true;
    if (palavraParecida(pa[0], pb[0])) return true;
    const [curto, longo] = pa.length <= pb.length ? [pa, pb] : [pb, pa];
    const emComum = curto.filter(p => longo.some(q => palavraParecida(p, q) || abreviacao(p, q) || inicial(p, q))).length;
    return emComum / curto.length >= 0.5;
}

/**
 * Decide se o conflito de nomes num mesmo CPF/CNPJ barra a emissão (#6160).
 *
 * Com o nome oficial do documento (Receita, via consultar-cpf-cnpj) a resposta é
 * firme: barra quando o nome do boleto não é o do dono do documento. Sem ele
 * (consulta falhou ou não achou), compara os dois nomes entre si, como antes.
 */
export function conflitoBarraEmissao(
    nomeEmitido: string | null | undefined,
    nomeRegistrado: string | null | undefined,
    nomeOficial: string | null | undefined,
): boolean {
    if (palavrasDoNome(nomeOficial).length > 0) return !nomesParecemMesmaPessoa(nomeEmitido, nomeOficial);
    return !nomesParecemMesmaPessoa(nomeEmitido, nomeRegistrado);
}

/**
 * O Sicoob recebe só as 50 primeiras letras do nome do pagador: o fluxo de emissão
 * no n8n manda `nome_cliente_boleto.substring(0, 50)`.
 */
export const LIMITE_NOME_PAGADOR_SICOOB = 50;

/**
 * Os dois nomes chegam ao banco como o MESMO pagador? Compara o que o banco recebe
 * (as 50 primeiras letras), sem acento, caixa, pontuação nem "DE/DA/LTDA":
 * "…ADMISSÃO DO CENTRO-SUL GOIAN" e "…GOIANO" chegam iguais ao Sicoob.
 */
export function mesmoNomeNoBanco(a: string | null | undefined, b: string | null | undefined): boolean {
    const noBanco = (nome: string | null | undefined) =>
        palavrasDoNome(String(nome ?? '').replace(/\s+/g, ' ').trim().slice(0, LIMITE_NOME_PAGADOR_SICOOB)).join(' ');
    return noBanco(a) === noBanco(b);
}

export interface BoletoDoDocumento {
    id: string | number;
    nome_cliente_boleto: string | null;
    emitido_em?: string | null;
}

/**
 * O boleto que define o nome que o banco tem hoje para um CPF/CNPJ: o emitido por
 * último, fora a própria fatura. O Sicoob guarda UM nome por documento e cada emissão
 * o reescreve — então é com esse, e não com qualquer boleto antigo, que o nome novo
 * tem de ser comparado. Sem data de emissão (boletos antigos) vai para o fim.
 */
export function boletoVigenteDoDocumento<T extends BoletoDoDocumento>(
    boletos: T[],
    faturaIdAtual?: string | number | null,
): T | null {
    const instante = (b: T) => {
        const t = b.emitido_em ? Date.parse(b.emitido_em) : NaN;
        return Number.isFinite(t) ? t : -Infinity;
    };
    const outros = boletos.filter(b => faturaIdAtual == null || String(b.id) !== String(faturaIdAtual));
    outros.sort((x, y) => (instante(y) - instante(x)) || (Number(y.id) - Number(x.id)));
    return outros[0] ?? null;
}

/** Ressalva de quem emite mesmo com pessoas diferentes no documento (#6160). */
export const RESSALVA_MIN_CARACTERES = 10;

export const ressalvaValida = (texto: string | null | undefined): boolean =>
    String(texto ?? '').trim().length >= RESSALVA_MIN_CARACTERES;
